"use client";

import { useQuery } from "@tanstack/react-query";
import { fetchOrderbook, type OrderbookLevel } from "@/lib/api";

const fmtPrice = (v: string) =>
  Number(v).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (v: string) =>
  Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 });

const Side = ({
  levels,
  side,
  maxQty,
}: {
  levels: OrderbookLevel[];
  side: "bid" | "ask";
  maxQty: number;
}) => (
  // Rendered in array order. Callers pass each side already sorted high→low so that the
  // best bid and best ask both sit adjacent to the spread row. That adjacency is how a
  // book is read, and getting it backwards puts the two prices that matter furthest apart.
  <div className="flex flex-col gap-px">
    {levels.map((l, i) => {
      const pct = maxQty > 0 ? (Number(l.quantity) / maxQty) * 100 : 0;
      return (
        <div key={`${l.price}-${i}`} className="relative flex justify-between px-2 py-0.5 text-xs">
          {/* Depth bar, encodes size positionally as well as by color, so it still
              reads without relying on hue alone. */}
          <div
            className={`absolute inset-y-0 right-0 ${side === "bid" ? "bg-buy/10" : "bg-sell/10"}`}
            style={{ width: `${pct}%` }}
            aria-hidden="true"
          />
          <span className={`tnum relative ${side === "bid" ? "text-buy" : "text-sell"}`}>
            {fmtPrice(l.price)}
          </span>
          <span className="tnum relative text-fg-secondary">{fmtQty(l.quantity)}</span>
        </div>
      );
    })}
  </div>
);

const Orderbook = ({ symbol }: { symbol: string }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["orderbook", symbol],
    queryFn: ({ signal }) => fetchOrderbook(symbol, 12, signal),
    // Polled, not streamed, the WebSocket gateway lands in Phase 1's remaining work.
    refetchInterval: 4_000,
  });

  if (isLoading) {
    return <div className="p-3 text-xs text-fg-muted">Loading order book…</div>;
  }
  // Guard the sides, not just `data`. The book is proxied live from Daya, so a transient
  // response with a null/absent side would otherwise crash `.map` and take down the page.
  const bids = Array.isArray(data?.bids) ? data.bids : [];
  const asks = Array.isArray(data?.asks) ? data.asks : [];

  if (isError || !data) {
    return <div className="p-3 text-xs text-sell">Order book unavailable</div>;
  }

  const maxQty = Math.max(...[...bids, ...asks].map((l) => Number(l.quantity)), 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex justify-between border-b border-line px-2 py-1.5 text-[11px] uppercase tracking-wide text-fg-muted">
        <span>Price (NGN)</span>
        <span>Size (USD)</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Daya returns asks ascending (best first); reverse so the column reads high→low
            and the best ask lands at the bottom, against the spread. Bids already arrive
            descending, so they need no reversal. */}
        <Side levels={[...asks].reverse()} side="ask" maxQty={maxQty} />

        {data.stats && (
          <div className="my-1 flex items-baseline justify-between border-y border-line bg-raised px-2 py-1.5">
            <span className="tnum text-sm text-fg">{fmtPrice(data.stats.mid_price)}</span>
            <span className="tnum text-[11px] text-fg-muted">
              spread {fmtPrice(data.stats.spread)} ({data.stats.spread_percent}%)
            </span>
          </div>
        )}

        <Side levels={bids} side="bid" maxQty={maxQty} />
      </div>
    </div>
  );
};

export default Orderbook;
